import { Meter } from "@base-ui/react/meter";

import {
  readVolatility,
  VOLATILITY_LEVELS,
  VOLATILITY_MAX,
  VOLATILITY_WORDS,
  type VolatilityLevel,
} from "@loot-os/api-contract";

import { cn } from "../foundations/cn.js";

/** The lit band's stretch of the ramp: from its stop to the next. Complete literals for Tailwind. */
const LIT: Record<VolatilityLevel, string> = {
  low: "from-volatility-0 to-volatility-1",
  medium: "from-volatility-1 to-volatility-2",
  high: "from-volatility-2 to-volatility-3",
  max: "from-volatility-3 to-volatility-4",
};

export interface VolatilityMeterProps {
  /** The box's volatility, 0 (mild) to `max` (wild). Missing or negative (unrated): no meter. */
  value: number | null | undefined;
  /** The top of the scale; `VOLATILITY_MAX` (16) by default. */
  max?: number;
  /** The meter's name, read first; the default copy's ("Volatility") when left out. */
  label?: string;
  /** Each level's word, shown and read in place of a number; the default copy's when left out. */
  levels?: Partial<Record<VolatilityLevel, string>>;
  /** Shows the name and the level's word above the bar; a box card leaves them out. */
  showLabel?: boolean;
  size?: "sm" | "md";
  className?: string;
}

/**
 * How swingy a box is: four bands from low to max, the band its value falls in lit, a marker at
 * the exact value. Screen readers hear the name and the level's word, never a number. Renders
 * nothing for a box with no rating.
 */
export function VolatilityMeter({
  value,
  max = VOLATILITY_MAX,
  label = VOLATILITY_WORDS.label,
  levels,
  showLabel = false,
  size = "sm",
  className,
}: VolatilityMeterProps) {
  const reading = readVolatility(value, max);
  if (!reading) return null;
  const word = levels?.[reading.level] || VOLATILITY_WORDS.levels[reading.level];
  return (
    <Meter.Root
      value={reading.value}
      min={0}
      max={max}
      locale="en-US"
      aria-label={showLabel ? undefined : label}
      getAriaValueText={() => word}
      data-level={reading.level}
      className={cn("flex flex-col gap-1.5", className)}
    >
      {showLabel && (
        <div className="flex items-baseline justify-between gap-3">
          <Meter.Label className="font-body text-body-b3-strong text-text-secondary">
            {label}
          </Meter.Label>
          <span aria-hidden className="font-body text-body-b3-strong text-text-primary">
            {word}
          </span>
        </div>
      )}
      <div aria-hidden className={cn("flex w-full gap-1", size === "sm" ? "h-1" : "h-1.5")}>
        {VOLATILITY_LEVELS.map((level, band) => (
          <span
            key={level}
            className={cn(
              "relative flex-1 rounded-pill",
              band === reading.band
                ? cn("bg-linear-to-r rtl:bg-linear-to-l", LIT[level])
                : "bg-bg-hover",
            )}
          >
            {band === reading.band && (
              <span
                style={{ left: `${reading.position * 100}%` }}
                className={cn(
                  "absolute top-1/2 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-pill bg-text-primary outline-1 outline-bg-surface",
                  size === "sm" ? "h-2.5" : "h-3",
                )}
              />
            )}
          </span>
        ))}
      </div>
    </Meter.Root>
  );
}
